// One transient, in-memory journey. No form answers or program data are cached.
let journey = null;
let returning = false;
let pending = null;
let settleMotion = null;
const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const cardParts = { host: '.og-host', title: '.og-story h3', subtitle: '.og-story h4', description: '.og-story>p' };
const coverParts = { host: '.program-cover-org', title: '.program-cover-story h1', subtitle: '.program-cover-story h2', description: '.program-cover-description' };
const incubatorParts = { title: '.ipc-story h2', subtitle: '.ipc-story h3', description: '.ipc-story>p' };
const builderParts = { title: '.pb-inline-name', subtitle: '.pb-inline-headline', description: '.program-cover-description' };
let builderId = null;
const visible = el => el && el.getBoundingClientRect().width && el.getBoundingClientRect().height;
const visualStyles = ('display position inset width height min-width min-height max-width max-height box-sizing margin padding gap flex flex-direction flex-wrap align-items justify-content align-self order grid-template-columns border border-radius background box-shadow opacity overflow overflow-x overflow-y color font-family font-size font-weight font-style line-height letter-spacing text-align text-transform text-decoration white-space overflow-wrap word-break text-overflow -webkit-line-clamp -webkit-box-orient object-fit object-position clip clip-path vertical-align list-style appearance -webkit-appearance transform transform-origin z-index visibility fill stroke stroke-width').split(' ');
function copy(el) {
  const clone = el.cloneNode(true);
  const originals = [el, ...el.querySelectorAll('*')];
  const copies = [clone, ...clone.querySelectorAll('*')];
  originals.forEach((node, i) => {
    const style = getComputedStyle(node);
    copies[i].style.cssText = visualStyles.map(key => `${key}:${style.getPropertyValue(key)}`).join(';') + ';animation:none;transition:none;resize:none;outline:none';
    copies[i].removeAttribute('id');
    copies[i].removeAttribute('data-testid');
    if (node instanceof HTMLTextAreaElement || node instanceof HTMLInputElement) copies[i].value = node.value;
  });
  return clone;
}
function snapshot(el, selectors) {
  const rect = el.getBoundingClientRect();
  const clone = copy(el);
  const background = backgroundOnly(el);
  clone.querySelectorAll('a,button,input').forEach(node => node.setAttribute('tabindex', '-1'));
  const parts = Object.entries(selectors).flatMap(([key, selector]) => {
    const node = el.querySelector(selector);
    if (!visible(node)) return [];
    clone.querySelector(selector)?.style.setProperty('opacity', '0');
    return [{ key, clone: copy(node), rect: node.getBoundingClientRect(), text: (node.value || node.textContent || '').trim(), fontSize: parseFloat(getComputedStyle(node).fontSize) || 16 }];
  });
  return { rect, clone, parts, background, radius: getComputedStyle(el).borderRadius }; 
}
// Background layers retain their own aspect ratio; only the clipping aperture morphs.
// This avoids stretched cover photography and per-frame text reflow.
function backgroundOnly(el) {
  const bg = document.createElement('div');
  bg.style.cssText = `position:absolute;overflow:hidden;background:${getComputedStyle(el).background};pointer-events:none`;
  const image = el.querySelector('.og-cover,.ipc-cover,[data-testid^="cover-image-"]');
  if (image) {
    const img = image.cloneNode(true);
    img.removeAttribute('data-testid');
    img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;max-width:none';
    bg.append(img);
  }
  const shade = el.querySelector('.og-shade,.ipc-shade,.program-cover-scrim,.program-cover-fallback');
  if (shade) { const scrim = copy(shade); scrim.style.cssText += ';position:absolute;inset:0;width:100%;height:100%'; bg.append(scrim); }
  return bg;
}
function layer(snap) {
  const root = document.createElement('div');
  root.setAttribute('aria-hidden', 'true');
  root.setAttribute('inert', '');
  root.dataset.programCoverMotion = '';
  root.style.cssText = 'position:fixed;inset:0;z-index:2147483000;pointer-events:none;overflow:hidden';
  const frame = document.createElement('div');
  const r = snap.rect;
  frame.style.cssText = `position:absolute;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;overflow:hidden;border-radius:${snap.radius};background:#19242b`;
  snap.clone.style.cssText += `;position:absolute;inset:0;margin:0;width:${r.width}px;height:${r.height}px;transform:none;max-width:none`;
  // Keep the complete card visible while the destination is being prepared.
  // Independent text layers prevent a blank card during a slow request.
  for (const part of snap.parts) {
    const ghost = part.clone.cloneNode(true);
    ghost.style.cssText += `;position:absolute;left:${part.rect.left-r.left}px;top:${part.rect.top-r.top}px;width:${part.rect.width}px;height:${part.rect.height}px;margin:0`;
    frame.append(ghost);
  }
  frame.prepend(snap.clone); root.append(frame); document.body.append(root);
  return { root, frame };
}
function clear() {
  settleMotion?.();
  if (!pending) return;
  clearTimeout(pending.timer);
  pending.root.remove();
  pending = null;
}
function begin(el, selectors, direction) {
  clear();
  if (!visible(el) || reduce()) return;
  const snap = snapshot(el, selectors);
  pending = { ...layer(snap), snap, direction };
  pending.timer = setTimeout(clear, 9000); // Failed requests must never leave an overlay behind.
}
export function openProgramCover(event, slug, navigate, ui) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
  event.preventDefault();
  const card = event.currentTarget.closest('.og-card');
  const scroll = card?.closest('.startup-scroll, .fb-scroll, main');
  returning = false;
  journey = { slug, ui, scroll: scroll?.scrollTop || 0, windowScroll: window.scrollY };
  // Remember every actual scroll container rather than assuming the app scrolls the window.
  journey.scrolls = [];
  for (let node = card?.parentElement; node; node = node.parentElement) {
    if (node.scrollTop) journey.scrolls.push({ className: node.className, top: node.scrollTop });
  }
  begin(card, cardParts, 'open');
  navigate(`/apply/${encodeURIComponent(slug)}?source=programs`);
}
export const programGalleryReturn = () => returning ? journey?.ui || null : null;
export function returnProgramCover(slug) {
  if (journey?.slug !== slug) return;
  returning = true;
  const cover = document.querySelector('[data-testid="cover-carousel-panel"]');
  const parent = cover?.parentElement;
  const original = parent?.getAttribute('style');
  if (cover && !visible(cover) && parent) parent.style.cssText += ';display:block;position:fixed;inset:0;width:100vw;height:100dvh';
  begin(cover, coverParts, 'back');
  if (parent) { if (original === null) parent.removeAttribute('style'); else parent.setAttribute('style', original); }
  const form = document.querySelector('.program-form-surface');
  if (visible(form) && pending && !reduce()) {
    const ghost = copy(form), r = form.getBoundingClientRect();
    ghost.style.cssText += `;position:absolute;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;margin:0`;
    pending.root.append(ghost);
    ghost.animate([{opacity:1},{opacity:0}],{duration:220,fill:'forwards'}).finished.then(()=>ghost.remove()).catch(()=>ghost.remove());
  }
}
export function finishProgramCover(direction, slug) {
  if (direction === 'back' && !pending) { returning = false; return; }
  if (journey?.slug !== slug || pending?.direction !== direction) return;
  const target = direction === 'open'
    ? document.querySelector('[data-testid="cover-carousel-panel"]')
    : [...document.querySelectorAll('.og-card')].find(el => el.querySelector('a')?.getAttribute('href') === `/apply/${encodeURIComponent(slug)}?source=programs`);
  if (!visible(target)) { clear(); return; }
  if (direction === 'back') returning = false;
  morphTo(target, direction === 'open' ? coverParts : cardParts, direction === 'open' ? document.querySelector('.program-form-surface') : null, direction);
}
function morphTo(target, selectors, form, direction) {
  if (!pending || !visible(target)) { clear(); return; }
  const active = pending;
  pending = null; clearTimeout(active.timer);
  if (reduce()) { active.root.remove(); return; }
  const { root, frame, snap } = active;
  const dest = target.getBoundingClientRect(), start = snap.rect;
  const duration = direction === 'back' || direction === 'builder-back' ? 580 : 720;
  const options = { duration, easing: 'cubic-bezier(.22,.8,.24,1)', fill: 'both' };
  const animations = [];
  frame.replaceChildren();
  frame.style.cssText = 'position:absolute;inset:0;overflow:hidden;will-change:clip-path';
  const aperture = (r, radius) => `inset(${r.top}px ${innerWidth-r.right}px ${innerHeight-r.bottom}px ${r.left}px round ${radius || '0px'})`;
  animations.push(frame.animate([{clipPath:aperture(start,snap.radius)},{clipPath:aperture(dest,getComputedStyle(target).borderRadius)}],options));
  const sourceBg = snap.background, destBg = backgroundOnly(target);
  const place = (node,r) => { node.style.cssText += `;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;transform-origin:center;will-change:transform,opacity`; frame.append(node); };
  place(sourceBg,start); place(destBg,dest);
  const scale = Math.max(dest.width/start.width,dest.height/start.height);
  const inverse = Math.max(start.width/dest.width,start.height/dest.height);
  const dx = (dest.left+dest.width/2)-(start.left+start.width/2), dy = (dest.top+dest.height/2)-(start.top+start.height/2);
  sourceBg.animate([{transform:'none',opacity:1},{transform:`translate(${dx}px,${dy}px) scale(${scale})`,opacity:1}],options);
  destBg.animate([{transform:`translate(${-dx}px,${-dy}px) scale(${inverse})`,opacity:0},{transform:'none',opacity:1}],options);
  // Carry the rest of each cover too: logos, status badges, footer, sections and editor controls.
  // Shared text has its own moving layers; everything else gets a continuous handoff.
  const detailLayer = (el, r) => {
    const layer = copy(el), originals = [el,...el.querySelectorAll('*')], copies = [layer,...layer.querySelectorAll('*')];
    for (const node of el.querySelectorAll('.og-cover,.ipc-cover,[data-testid^="cover-image-"],.og-shade,.ipc-shade,.program-cover-scrim,.program-cover-fallback')) copies[originals.indexOf(node)].style.opacity='0';
    layer.style.cssText += `;position:absolute;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;margin:0;background:transparent;border-color:transparent;box-shadow:none;transform-origin:top left;pointer-events:none`;
    layer.dataset.programCoverDetails = '';
    frame.append(layer);
    return { layer, originals, copies };
  };
  const details = detailLayer(target,dest);
  for (const part of snap.parts) {
    let end = target.querySelector(selectors[part.key]);
    // VC covers reverse the title and invitation order. Follow matching content.
    const match = Object.values(selectors).map(selector=>target.querySelector(selector)).find(el=>visible(el)&&(el.value||el.textContent||'').trim()===part.text);
    if(match)end=match;
    if (!visible(end)) continue;
    details.copies[details.originals.indexOf(end)].style.opacity='0';
    const r = end.getBoundingClientRect(), a = part.rect;
    const incoming = copy(end);
    const fontRatio = (parseFloat(getComputedStyle(end).fontSize)||16)/part.fontSize;
    const position = (node,rect) => { node.style.cssText += `;position:absolute;left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px;margin:0;transform-origin:top left;will-change:transform,opacity;pointer-events:none`; root.append(node); };
    position(part.clone,a);position(incoming,r);
    // Each text layer keeps its final line breaks and scales uniformly, never stretches.
    const textMove = `translate(${r.left-a.left}px,${r.top-a.top}px) scale(${fontRatio})`;
    const reverseMove = `translate(${a.left-r.left}px,${a.top-r.top}px) scale(${1/fontRatio})`;
    animations.push(part.clone.animate([{transform:'none',opacity:1},{offset:.64,opacity:1},{offset:.76,opacity:0},{transform:textMove,opacity:0}],options));
    animations.push(incoming.animate([{transform:reverseMove,opacity:0},{offset:.74,opacity:0},{offset:.88,opacity:1},{transform:'none',opacity:1}],options));
  }
  animations.push(details.layer.animate([
    {opacity:0,transform:'translateY(6px)'},
    {offset:.18,opacity:0},
    {transform:'none',opacity:1}
  ], {...options,easing:'cubic-bezier(.3,.1,.4,1)'}));
  const oldVisibility = target.style.visibility;
  target.style.visibility = 'hidden';
  // Reveal the actual form alongside the cover, so the second column is never empty.
  const formAnimation = visible(form) ? form.animate(
    [{opacity:.35,transform:'translateY(6px)'},{opacity:1,transform:'none'}],
    {duration,easing:'cubic-bezier(.3,.1,.4,1)',fill:'both'}
  ) : null;
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true; settleMotion = null;
    root.remove(); target.style.visibility = oldVisibility;
    formAnimation?.cancel();
    if(direction==='back')target.querySelector('a')?.focus({preventScroll:true});
    if(direction==='builder-back')target.querySelector('[data-testid^="edit-"]')?.focus({preventScroll:true});
  };
  settleMotion = settle;
  Promise.allSettled(animations.map(a=>a.finished)).finally(settle);
}
function fadeQuestions(form) {
  form.animate([{opacity:0,transform:'translateY(5px)'},{opacity:1,transform:'none'}],{duration:240,easing:'ease-out'});
}
export function beginProgramBuilder(event, id) {
  builderId = id;
  begin(event.currentTarget.closest('.ipc-card'), incubatorParts, 'builder');
}
export function finishProgramBuilder(id) {
  if(builderId!==id || pending?.direction!=='builder')return;
  morphTo(document.querySelector('.pb-direct-canvas [data-testid="cover-carousel-panel"]'),builderParts,document.querySelector('.pb-direct-canvas .program-form-surface'),'builder');
}
export function closeProgramBuilder(close) {
  if(!builderId){close();return;}
  const cover=document.querySelector('.pb-direct-canvas [data-testid="cover-carousel-panel"]');
  if(visible(cover))begin(cover,builderParts,'builder-back');
  close();
  requestAnimationFrame(()=>{
    const card=document.querySelector(`[data-testid="program-card-${CSS.escape(builderId)}"]`);
    if(pending?.direction==='builder-back')morphTo(card,incubatorParts,null,'builder-back');
    builderId=null;
  });
}
export function restoreProgramGalleryScroll() {
  if (!returning || !journey) return;
  for (const saved of journey.scrolls || []) {
    [...document.querySelectorAll('[class]')].find(el => el.className === saved.className)?.scrollTo({top:saved.top,behavior:'instant'});
  }
  window.scrollTo({top:journey.windowScroll,behavior:'instant'});
}
export const returningProgramSlug = () => journey?.slug;

export const cancelProgramCover = () => clear();

export function revealProgramQuestions() {
  if (!journey || reduce()) return;
  requestAnimationFrame(() => {
    const form = document.querySelector('.program-form-surface');
    if (form) fadeQuestions(form);
  });
}
